import { getScoreColor } from '../utils';

/**
 * Component for rendering circular score display
 */
export class ScoreComponent {
  private readonly circleRadius = 54;
  private readonly circleCircumference = 2 * Math.PI * this.circleRadius;

  /**
   * Render score circle in a container
   * @param containerId - ID of container element
   * @param score - Score value (0-100)
   * @param animate - Whether to animate on first render
   */
  render(containerId: string, score: number, animate: boolean = true): void {
    const container = document.getElementById(containerId);
    if (!container) return;

    const offset = this.circleCircumference - (score / 100) * this.circleCircumference;
    const colorClass = getScoreColor(score);

    // Check if already rendered
    const existingCircle = container.querySelector('.progress-ring__circle');
    const existingText = container.querySelector('.text-3xl');

    if (existingCircle && existingText) {
      // Update existing display
      this.updateScore(existingCircle as SVGCircleElement, existingText as HTMLElement, score, offset, colorClass);
    } else {
      // First time rendering
      this.createScore(container, score, offset, colorClass, animate);
    }
  }

  /**
   * Create initial score display
   */
  private createScore(
    container: HTMLElement,
    score: number,
    offset: number,
    colorClass: string,
    animate: boolean
  ): void {
    container.innerHTML = `
      <svg class="w-full h-full" viewBox="0 0 120 120">
        <circle class="stroke-gray-700" stroke-width="12" fill="transparent" r="${this.circleRadius}" cx="60" cy="60"/>
        <circle class="progress-ring__circle ${colorClass}" stroke-width="12" stroke-linecap="round"
          fill="transparent" r="${this.circleRadius}" cx="60" cy="60"
          style="stroke-dasharray:${this.circleCircumference} ${this.circleCircumference};stroke-dashoffset:${animate ? this.circleCircumference : offset}"/>
      </svg>
      <div class="absolute inset-0 flex flex-col items-center justify-center">
        <span class="text-3xl font-extrabold text-white">${score}</span>
        <span class="text-xs font-medium text-gray-400">Score</span>
      </div>
    `;

    if (animate) {
      // Animate after a brief delay
      setTimeout(() => {
        const circle = container.querySelector('.progress-ring__circle') as SVGCircleElement;
        if (circle) {
          circle.style.strokeDashoffset = offset.toString();
        }
      }, 100);
    }
  }

  /**
   * Update existing score display
   */
  private updateScore(
    circle: SVGCircleElement,
    textElement: HTMLElement,
    score: number,
    offset: number,
    colorClass: string
  ): void {
    // Update circle color and offset
    circle.setAttribute('class', `progress-ring__circle ${colorClass}`);
    circle.style.strokeDashoffset = offset.toString();

    // Update text
    textElement.textContent = score.toString();
  }

  /**
   * Clear score display
   */
  clear(containerId: string): void {
    const container = document.getElementById(containerId);
    if (container) {
      container.innerHTML = '';
    }
  }
}
